//
// # ParasiticsPanel
//
// Panel showing parasitic extraction (PEX) results.
// Displays per-net resistance/capacitance with click-to-highlight functionality.
//
// Click a net row to highlight its routes in the layout.
// Click a coupling pair to highlight both nets.
//

import React, { useEffect, useState } from "react";
import {
  Zap,
  CheckCircle,
  EyeOff,
  Play,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useWorkspace, useWorkspaceSnapshot } from "../../context";
import type { NetSummary, CouplingPair } from "./types";
import "./parasiticsPanel.css";

// Format resistance in appropriate units (Ohms or kOhms)
function formatResistance(ohms: number): string {
  if (ohms >= 1000) {
    return `${(ohms / 1000).toFixed(2)}kΩ`;
  }
  return `${ohms.toFixed(2)}Ω`;
}

// Format capacitance in appropriate units (fF or pF)
function formatCapacitance(fF: number): string {
  if (fF >= 1000) {
    return `${(fF / 1000).toFixed(2)}pF`;
  }
  return `${fF.toFixed(2)}fF`;
}

// Format identKey for display
function formatIdentKey(identKey: string): string {
  try {
    const parsed = JSON.parse(identKey) as { path?: string[] };
    if (parsed.path && Array.isArray(parsed.path)) {
      return parsed.path.join("/");
    }
  } catch {
    // Fall through to return as-is
  }
  return identKey;
}

// Net row component
function NetRow({
  net,
  onHighlight,
}: {
  net: NetSummary;
  onHighlight: (netName: string) => void;
}) {
  return (
    <button
      className="parasitics-net-row"
      onClick={() => onHighlight(net.name)}
    >
      <div className="parasitics-net-name">{net.name}</div>
      <div className="parasitics-net-stats">
        <span className="parasitics-stat">
          R: {formatResistance(net.wireResistance + net.viaResistance)}
        </span>
        <span className="parasitics-stat">
          C:{" "}
          {formatCapacitance(net.groundCapacitance + net.couplingCapacitance)}
        </span>
      </div>
      <div className="parasitics-net-details">
        {net.segmentCount} seg, {net.viaCount} via
      </div>
    </button>
  );
}

// Coupling pair row component
function CouplingRow({
  pair,
  onHighlight,
}: {
  pair: CouplingPair;
  onHighlight: (netA: string, netB: string) => void;
}) {
  return (
    <button
      className="parasitics-coupling-row"
      onClick={() => onHighlight(pair.netA, pair.netB)}
    >
      <div className="parasitics-coupling-nets">
        {pair.netA} ↔ {pair.netB}
      </div>
      <div className="parasitics-coupling-value">
        {formatCapacitance(pair.capacitance)}
      </div>
    </button>
  );
}

export function ParasiticsPanel() {
  const workspace = useWorkspace();
  const snapshot = useWorkspaceSnapshot();
  const [running, setRunning] = useState(false);

  const [netsExpanded, setNetsExpanded] = useState(true);
  const [couplingExpanded, setCouplingExpanded] = useState(true);

  // Get PEX results from workspace
  const result = workspace.getPexResult();

  // Clear highlights when panel unmounts
  useEffect(() => {
    return () => {
      workspace.highlightParasiticsNet(null);
    };
  }, [workspace]);

  const handleRunPex = async () => {
    setRunning(true);
    try {
      await workspace.runPex();
    } finally {
      setRunning(false);
    }
  };

  const handleClearHighlights = () => {
    workspace.highlightParasiticsNet(null);
  };

  const handleHighlightNet = (netName: string) => {
    workspace.highlightParasiticsNet(netName);
  };

  const handleHighlightCoupling = (netA: string, netB: string) => {
    // Highlight both nets
    workspace.highlightParasiticsNet(netA, netB);
  };

  // Check if active editor is a layout
  const isLayoutActive =
    snapshot.activeIdent &&
    workspace.getActiveEditor()?.content.contentKind === "layout";

  const hasResults = result !== null;
  const totalNets = result?.netSummaries.length ?? 0;

  const buttonClass =
    "flex-1 px-3 py-1.5 rounded flex items-center justify-center gap-1.5 text-[11px] " +
    "bg-[var(--color-ide-surface-hover)] hover:bg-[var(--color-ide-surface-selected)] " +
    "text-[var(--color-ide-text)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

  return (
    <div className="parasitics-panel">
      {/* Panel header */}
      <div className="parasitics-header">
        <div className="parasitics-header-title">
          <Zap size={11} />
          PEX Results
          {result?.identKey && (
            <span className="parasitics-header-ident">
              — {formatIdentKey(result.identKey)}
            </span>
          )}
        </div>
      </div>

      {/* Content area */}
      <div className="parasitics-content">
        {/* Run PEX button */}
        {isLayoutActive && (
          <div className="p-2 flex gap-2 border-b border-[var(--color-ide-border)]">
            <button
              className={buttonClass}
              onClick={() => void handleRunPex()}
              disabled={running}
              title="Extract parasitics from layout routes"
            >
              <Play size={12} />
              Run PEX
            </button>
          </div>
        )}

        {running && (
          <div className="parasitics-message">Extracting parasitics...</div>
        )}

        {!isLayoutActive && (
          <div className="parasitics-message">
            Open a layout to extract parasitics.
          </div>
        )}

        {isLayoutActive && !running && !hasResults && (
          <div className="parasitics-message">
            No PEX results. Click Run PEX to extract R/C values from layout
            routes.
          </div>
        )}

        {!running && result !== null && (
          <div className="p-2">
            {/* Status banner */}
            <div className="px-3 py-2 mb-2 rounded flex items-center gap-2 text-[11px] bg-green-500/10 text-green-400">
              <CheckCircle size={14} />
              {totalNets} Net{totalNets !== 1 ? "s" : ""} Extracted
            </div>

            {/* Summary stats */}
            <div className="parasitics-summary">
              <div className="parasitics-summary-row">
                <span>Segments:</span>
                <span>{result.stats.segmentsExtracted}</span>
              </div>
              <div className="parasitics-summary-row">
                <span>Vias:</span>
                <span>{result.stats.viasExtracted}</span>
              </div>
              <div className="parasitics-summary-row">
                <span>Coupling pairs:</span>
                <span>{result.stats.couplingPairsFound}</span>
              </div>
              <div className="parasitics-summary-divider" />
              <div className="parasitics-summary-row">
                <span>Total Wire R:</span>
                <span>{formatResistance(result.totalWireResistance)}</span>
              </div>
              <div className="parasitics-summary-row">
                <span>Total Via R:</span>
                <span>{formatResistance(result.totalViaResistance)}</span>
              </div>
              <div className="parasitics-summary-row">
                <span>Total Ground C:</span>
                <span>{formatCapacitance(result.totalGroundCapacitance)}</span>
              </div>
              <div className="parasitics-summary-row">
                <span>Total Coupling C:</span>
                <span>
                  {formatCapacitance(result.totalCouplingCapacitance)}
                </span>
              </div>
            </div>

            {/* Clear highlights button */}
            <button
              className="parasitics-clear-btn"
              onClick={handleClearHighlights}
            >
              <EyeOff size={10} />
              Clear highlights
            </button>

            {/* Per-net breakdown */}
            {result.netSummaries.length > 0 && (
              <div className="parasitics-section">
                <button
                  className="parasitics-section-header"
                  onClick={() => {
                    setNetsExpanded(!netsExpanded);
                  }}
                >
                  {netsExpanded ? (
                    <ChevronDown size={12} />
                  ) : (
                    <ChevronRight size={12} />
                  )}
                  Nets ({result.netSummaries.length})
                </button>
                {netsExpanded && (
                  <div className="parasitics-section-content">
                    {result.netSummaries.map((net) => (
                      <NetRow
                        key={net.name}
                        net={net}
                        onHighlight={handleHighlightNet}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Top coupling pairs */}
            {result.topCouplingPairs.length > 0 && (
              <div className="parasitics-section">
                <button
                  className="parasitics-section-header"
                  onClick={() => {
                    setCouplingExpanded(!couplingExpanded);
                  }}
                >
                  {couplingExpanded ? (
                    <ChevronDown size={12} />
                  ) : (
                    <ChevronRight size={12} />
                  )}
                  Top Coupling ({result.topCouplingPairs.length})
                </button>
                {couplingExpanded && (
                  <div className="parasitics-section-content">
                    {result.topCouplingPairs.map((pair, idx) => (
                      <CouplingRow
                        key={`${pair.netA}-${pair.netB}-${idx}`}
                        pair={pair}
                        onHighlight={handleHighlightCoupling}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Stats footer */}
            <div className="mt-3 px-2 text-[10px] text-[var(--color-ide-text-dim)]">
              <div>Routes processed: {result.stats.routesProcessed}</div>
              <div>
                Extraction time:{" "}
                {(result.stats.extractionTimeUs / 1000).toFixed(1)}ms
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
